import { Injectable } from '@angular/core';
import { UserLogged } from '@fichaje/interfaces/userLogged.interface';
import { LogicChain } from '@fichaje/logic-dialogs-v2/components/logic-dialogs.interface';
import { ProneFirstLoad } from '@fichaje/interfaces/schedule.interface';

export type LocalStoreDataKeys = 'LogicChain'|'userLogged'|'proneFirstLoadStorage';

type LocalStoreDataMap = {
  LogicChain: LogicChain;
  userLogged: UserLogged;
  proneFirstLoadStorage: ProneFirstLoad;
};


@Injectable({
  providedIn: 'root',
})
export class HelperService {
  nameShortener(name: string, complete: 0 | 1 | 2 | 3 = 0) {
    if (complete == 3) return name;
    const splited = name.split(' ');
    let result = '';
    for (let i = 0; i < splited.length && i < 3; i++) {
      const phrase = splited[i];
      const lettersArr = phrase.split('');
      if (lettersArr && lettersArr.length > 0 && complete == 0) {
        result += lettersArr[0];
      } else if (lettersArr && lettersArr.length > 0 && complete > 0) {
        result += i + 1 == complete ? phrase + ' ' : lettersArr[0] + '. ';
      }
    }
    return result;
  }

  reverseArr(arr: any[] | undefined): any[] {
    if (arr && arr.length > 0) {
      const clone = structuredClone(arr);
      return clone.reverse();
    }
    return [];
  }

  sortArrOfNumbers(numbers: number[], ascendent: boolean = true): number[] {
    return [...numbers].sort((a, b) => (ascendent ? a - b : b - a));
  }

  arrToKeyBasedObj(arr:any[], keyParam:string, prefix:string = ''){
    // Esta funcion coge un arreglo de diccionarios (por ejemplo libros donde hay un parametro
    // authorId) y en base a estos crea un nuevo diccionario donde agrupa segun un keyParam
    // seleccionado. De nuestro ejemplo crearía un objeto donde cada parametro sería el de
    // un cada authorId y este contendira todos los diccionarios de libros que le corresponde.
    // Opción a agregar un prefijo para evitar nombres de parámetros numéricos.
    if(!arr || arr.length < 1 || !keyParam) return;
    let obj:any = {};
    for (const el of arr) {
      obj[prefix+el[keyParam]] ??= obj[prefix+el[keyParam]] = [];
      obj[prefix+el[keyParam]].push(el)
    }
    return obj;
  }

  getNumberBasedOnOtherNumber(datoInicial: number | string, limit: number): number {
    if (limit <= 0) {
      // throw new Error("El 'limit' debe ser un número positivo mayor que cero.");
      return 1 //! ver una mejor opcion
    }

    // 1. Convertir el datoInicial a un string para la función hash
    const dataString = String(datoInicial);

    // 2. Función hash simple para generar un número a partir del string
    // Esta es una variante del algoritmo de hash DJB2, conocido por su simplicidad y efectividad.
    let hash = 0;
    for (let i = 0; i < dataString.length; i++) {
      const char = dataString.charCodeAt(i);
      hash = ((hash << 5) - hash) + char; // hash * 31 + char
      hash |= 0; // Convierte a un entero de 32 bits con signo
    }

    // Asegurar que el hash sea positivo (ya que modulo con negativos puede dar negativo)
    hash = Math.abs(hash);

    // 3. Escalar el hash al rango [1, limit]
    // (hash % limit) nos da un número entre 0 y limit-1.
    // Sumando 1, obtenemos un número entre 1 y limit.
    const resultado = (hash % limit) + 1;

    return resultado;

  }

  public zigzag(limit: number, index: number): number {
    if (limit <= 0) {
      throw new Error('El límite debe ser mayor que 0');
    }

    // Calculamos el ciclo completo (subida + bajada)
    const cycleLength = limit * 2 - 2;

    // Si el límite es 1, siempre retorna 0
    if (limit === 1) {
      return 0;
    }

    // Obtenemos la posición dentro del ciclo
    const position = index % cycleLength;

    // Si estamos en la primera mitad del ciclo (subiendo)
    if (position < limit) {
      return position;
    }
    // Si estamos en la segunda mitad del ciclo (bajando)
    else {
      return cycleLength - position;
    }
  }

  public totalizeArrayObjs(arrObj:any[], paramNameToAccumulate:string): number{
    // Recibe un array de diccionarios que cuentan con un parametro numerico que se quiera
    // totalizar, por ejemplo, un array de facturas donde tienen un parametro 'total'. Luego
    // suma acumulativamente los parametros y devuelve el gran total ( del ejemplo, sumaria
    // cada uno de los totales de cada factura y devolveria el total de todas las facturas)
    if ( !(arrObj && arrObj.length > 0)  ) return 0
    let total:number = 0;
    for (const el of arrObj) {
      total += el[paramNameToAccumulate]
    }
    return total
  }

  public filterValidValuesFromObj<T extends Record<string, any>>(
    // Función para filtrar diccionario removiendo valores null, undefined, cadenas vacías,
    // arrays vacíos '[]' u objetos vacíos '{}'
    dictionary: T
  ): Partial<T> {
    const filtered: Partial<T> = {};

    for (const [key, value] of Object.entries(dictionary)) {
      // Verificar condiciones más extensas
      if (
        value !== null &&
        value !== undefined &&
        value !== "" &&
        !(Array.isArray(value) && value.length === 0) &&
        !(typeof value === 'object' && Object.keys(value).length === 0)
      ) {
        (filtered as any)[key] = value;
      }
    }
    return filtered;
  }

  //!! revisar esta version recursiva que evita referencias circulares....
  public filterValidValuesFromObj2<T extends Record<string, any>>(
    dictionary: T,
    visited = new WeakSet<object>() // Protección contra referencias circulares
  ): Partial<T> {
    // Validación inicial
    if (!dictionary || typeof dictionary !== 'object') {
      return {} as Partial<T>;
    }

    // Protección contra referencias circulares
    if (visited.has(dictionary)) {
      console.warn('Referencia circular detectada, omitiendo objeto');
      return {} as Partial<T>;
    }

    // Marcar objeto como visitado
    visited.add(dictionary);

    const filtered: Partial<T> = {};

    for (const [key, value] of Object.entries(dictionary)) {
      // Saltar valores nulos, indefinidos o cadenas vacías
      if (value === null || value === undefined || value === '') {
        continue;
      }

      // Filtrar arrays vacíos
      if (Array.isArray(value)) {
        if (value.length === 0) {
          continue;
        }
        // Procesar arrays recursivamente
        const filteredArray = value
          .map(item => {
            if (item && typeof item === 'object' && !Array.isArray(item)) {
              return this.filterValidValuesFromObj2(item, visited);
            }
            return item;
          })
          .filter(item => {
            // Filtrar items inválidos del array
            if (item === null || item === undefined || item === '') {
              return false;
            }
            if (typeof item === 'object' && Object.keys(item).length === 0) {
              return false;
            }
            return true;
          });

        if (filteredArray.length > 0) {
          (filtered as any)[key] = filteredArray;
        }
        continue;
      }

      // Procesar objetos anidados recursivamente
      if (typeof value === 'object' && value !== null) {
        // Evitar procesar tipos especiales (Date, RegExp, etc.)
        if (value instanceof Date || value instanceof RegExp) {
          (filtered as any)[key] = value;
          continue;
        }

        const nestedFiltered = this.filterValidValuesFromObj2(value, visited);

        // Solo incluir si el objeto filtrado tiene propiedades
        if (Object.keys(nestedFiltered).length > 0) {
          (filtered as any)[key] = nestedFiltered;
        }
        continue;
      }

      // Incluir valores primitivos válidos
      (filtered as any)[key] = value;
    }

    return filtered;
  }

  // ? #########################################################
  // ? ############## COMPONENTE LISTAS Y FILTROS ##############
  // ? #########################################################
  public idToIdstr(id: number | string): string {
    return 'id' + id;
  }

  public findItemById(
    items: any[],
    id?: string | number,
    idParam: string = 'id',
    nestingParam: string = 'children'
  ): any | null {
    // Find items in an array of objects that can include desting childrens
    if (id == null || id == undefined || id == '' || !items) return null;
    let itemsCloned = structuredClone(items);
    for (const item of itemsCloned) {
      if (String(item[idParam]) === String(id)) {
        return item; // Si el ID coincide, lo devolvemos
      }
      if (item[nestingParam] && Array.isArray(item[nestingParam])) {
        const found = this.findItemById(
          item[nestingParam],
          id,
          idParam,
          nestingParam
        );
        if (found) {
          return found; // Si se encuentra en los hijos, lo devolvemos
        }
      }
    }
    return null; // Si no se encuentra, retornamos null
  }

  public createParentChildNesting<T extends { id: number } & Record<string, any>>(
    items: T[],
    nestingParamName: string,
    parentParamName: string
  ): T[] {
    // Creamos un mapa de los items, donde la key es el id de cada item
    const itemsById = new Map<number, T>();

    // Inicializamos todos los items en el mapa
    items.forEach((item) => {
      itemsById.set(item.id, { ...item, [nestingParamName]: [] });
    });

    // Ahora construimos la jerarquía
    const root: T[] = [];

    items.forEach((item) => {
      const parentId = item[parentParamName];
      if (parentId === 0 || parentId === null || parentId === undefined) {
        // Si no tiene parent (0, null o undefined según backend), es una raíz
        root.push(itemsById.get(item.id)!);
      } else {
        // Si tiene parent - parentParamName, lo agregamos a su lista de children - nestingParamName
        const parent = itemsById.get(parentId);
        if (parent) {
          parent[nestingParamName]?.push(itemsById.get(item.id));
        }
      }
    });

    return root;
  }

  public addNestingLevels(
    data: any[] | undefined,
    nestingParamName: string,
    nestinglevel: number = 0
  ): any[] {
    //? recorre un diccionario anidado y agrega el parametro 'nestinglevel' con el numero
    //? indicativo del nivel de anidación del respectivo item.

    if (!data) return [];
    const newData = data.map((item) => {
      const newItem = {
        ...item,
        nestinglevel,
      };
      newItem[nestingParamName] = item[nestingParamName]
        ? this.addNestingLevels(
            item[nestingParamName],
            nestingParamName,
            nestinglevel + 1
          )
        : [];
      return newItem;
    });
    return newData;
  }

  public cleanMarkedTags(
    item: any,
    searchingParamName: string = 'name',
    nestingParamName: string = 'children'
  ): any {
    //? En un diccionario, revisa el parametro indicado en searchingParamName y
    //? elimina las etiquetas <mark></mark>

    if (!item) return item;

    // Expresión regular para eliminar las etiquetas <mark>
    const unmarkRegex = /<\/?mark>/gi;

    // Clonar el objeto para evitar modificar el original
    const cleanedItem = { ...item };

    // Si el parámetro de búsqueda existe y es un string, limpiamos las etiquetas <mark>
    if (typeof cleanedItem[searchingParamName] === 'string') {
      cleanedItem[searchingParamName] = cleanedItem[searchingParamName].replace(
        unmarkRegex,
        ''
      );
    }

    // Si el objeto tiene elementos anidados, aplicamos la función recursivamente
    if (Array.isArray(cleanedItem[nestingParamName])) {
      cleanedItem[nestingParamName] = cleanedItem[nestingParamName].map(
        (child) =>
          this.cleanMarkedTags(child, searchingParamName, nestingParamName)
      );
    }

    return cleanedItem;
  }

  public filterNested(
    items?: any[],
    criterio?: string,
    searchingParamName: string = 'name',
    nestingParamName: string = 'children'
  ): any[] {
    // Esta funcion busca un texto 'criterio' dentro de un arreglo de diccionarios anidado
    // items = arreglo de diccionarios,
    // searchingParamName = nombre del parametro en el que se realiza la búsqueda,
    // nestingParamName = nombre del parametro donde se anida.

    if (!items || items.length < 1) return []; // Si no hay items, retornamos un arreglo vacío
    if (!criterio || criterio == '') return items; // Si no hay criterio, retornamos los items originales
    const resultado: any[] = [];
    const itemsCloned = structuredClone(items);

    const escapeRegExp = (text: string) =>
      text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); // Escapar caracteres especiales

    // Filtramos los hijos recursivamente
    itemsCloned.forEach((item) => {
      const filteredChildren =
        item[nestingParamName] && item[nestingParamName].length > 0
          ? this.filterNested(
              item[nestingParamName],
              criterio,
              searchingParamName,
              nestingParamName
            )
          : [];
      // : itemWithoutChildren(item);

      const itemValue = item[searchingParamName]; // Evitar errores si el valor es undefined o null

      if (typeof itemValue === 'string') {
        const regex = new RegExp(`(${escapeRegExp(criterio)})`, 'gi'); // Expresión regular segura
        const highlightedName = itemValue.replace(regex, '<mark>$1</mark>');

        if (
          itemValue.toLowerCase().includes(criterio.toLowerCase()) ||
          filteredChildren.length > 0
        ) {
          resultado.push({
            ...item,
            [searchingParamName]: highlightedName, // Nombre con las coincidencias resaltadas
            [nestingParamName]: filteredChildren,
            // [nestingParamName]: filteredChildren.length > 0 ? filteredChildren : itemConHijosSinNietos,
          });
        }
      }
    });

    return resultado;
  }

  public filterNestedShowChildren(
    items?: any[],
    criterio?: string,
    searchingParamName: string = 'name',
    nestingParamName: string = 'children'
  ): any[] {
    if (!items) return [];
    const itemsCloned = structuredClone(items);
    if (!criterio) return itemsCloned;

    const criterioLower = criterio.toLowerCase();
    const regex = new RegExp(`(${this.escapeRegExp(criterio)})`, 'gi');

    const resultado: any[] = [];

    itemsCloned.forEach((item: any) => {
      const itemValue = item[searchingParamName];
      const isItemValueString = typeof itemValue === 'string';

      let highlightedItemName = itemValue;
      let finalChildren: any[] | undefined = undefined;
      let includeItem = false;

      const directMatch =
        isItemValueString && itemValue.toLowerCase().includes(criterioLower);

      if (directMatch) {
        // El item actual coincide directamente con el criterio
        includeItem = true;
        if (isItemValueString) {
          highlightedItemName = itemValue.replace(regex, '<mark>$1</mark>');
        }

        const originalItemChildren = item[nestingParamName];
        if (originalItemChildren && Array.isArray(originalItemChildren)) {
          finalChildren = originalItemChildren
            .map((child: any) => {
              // Verificar si la rama de este 'child' contiene alguna coincidencia
              const childBranchHasMatches = this.checkBranchForMatches(
                child,
                searchingParamName,
                nestingParamName,
                criterioLower
              );

              if (childBranchHasMatches) {
                // Si hay coincidencias en la rama del 'child', procesarla recursivamente con filterNestedShowChildren.
                // filterNestedShowChildren espera un array, por eso pasamos [child].
                // El resultado será un array (con el 'child' procesado si se mantiene, o vacío).
                const processedChildArray = this.filterNestedShowChildren(
                  [child], // filterNestedShowChildren clonará este 'child' internamente
                  criterio,
                  searchingParamName,
                  nestingParamName
                );
                // Devolvemos el primer elemento si el array no está vacío, sino null.
                return processedChildArray.length > 0
                  ? processedChildArray[0]
                  : null;
              } else {
                // Si no hay coincidencias en la rama del 'child', incluir el 'child'
                // pero podar a sus propios hijos (nietos del 'item' original).
                // El nombre del 'child' no se resalta ya que no hubo coincidencia en su rama.
                return {
                  ...child,
                  [searchingParamName]: child[searchingParamName], // Nombre original del 'child'
                  [nestingParamName]: [], // Podar los hijos del 'child'
                };
              }
            })
            .filter((c) => c !== null); // Eliminar los 'null' si alguna rama del 'child' fue completamente descartada
        } else {
          finalChildren = []; // No hay hijos o no es un array
        }
      } else {
        // El item actual NO coincide directamente, verificar sus descendientes
        const originalItemChildren = item[nestingParamName];
        if (originalItemChildren && Array.isArray(originalItemChildren)) {
          const filteredRecursiveChildren = this.filterNestedShowChildren(
            originalItemChildren,
            criterio,
            searchingParamName,
            nestingParamName
          );
          if (filteredRecursiveChildren.length > 0) {
            includeItem = true;
            finalChildren = filteredRecursiveChildren;
          }
        }
      }

      if (includeItem) {
        resultado.push({
          ...item,
          [searchingParamName]: highlightedItemName,
          [nestingParamName]: finalChildren || [],
        });
      }
    });

    return resultado;
  }

  private escapeRegExp(text: string): string {
    return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  private checkBranchForMatches(
    itemToCheck: any,
    searchingParamName: string,
    nestingParamName: string,
    criterioLower: string // Criterio precalculado en minúsculas
  ): boolean {
    // Esta función verifica si el item dado o alguno de sus descendientes coincide con el criterio.
    // No realiza resaltado ni modificaciones, solo chequea la existencia de coincidencias.
    if (!itemToCheck) return false;

    const itemValue = itemToCheck[searchingParamName];
    if (
      typeof itemValue === 'string' &&
      itemValue.toLowerCase().includes(criterioLower)
    ) {
      return true; // Coincidencia directa en itemToCheck
    }

    const children = itemToCheck[nestingParamName];
    if (children && Array.isArray(children)) {
      // Usamos .some() para una búsqueda eficiente que termina pronto si encuentra una coincidencia
      return children.some((child) =>
        this.checkBranchForMatches(
          child,
          searchingParamName,
          nestingParamName,
          criterioLower
        )
      );
    }
    return false; // No hay coincidencia en itemToCheck ni en sus descendientes
  }
  privateText(str:string|undefined, visibleExtremes:number): string|undefined{
    if (!str) return undefined;
    const totalLength = str.length;
    if (!visibleExtremes || totalLength <= visibleExtremes*2) return str;
    const hiddenCharts = totalLength - visibleExtremes*2
    let hiddenText = '';
    for (let i = 0; i < str.length; i++) {
      const ch = str[i];
      hiddenText += i < visibleExtremes || i+1 > visibleExtremes + hiddenCharts ? ch : '*'
    }
    return hiddenText
  }

  JSONtryParse<T = unknown>(value: unknown): T | undefined {
    if (value == null) return undefined;

    if (typeof value !== 'string') {
      return value as T;
    }

    const sanitizedValue = value.trim().replace(/^\uFEFF/, '');
    if (!sanitizedValue) return undefined;

    try {
      return JSON.parse(sanitizedValue) as T;
    } catch (error) {
      return undefined;
      //console.error(`Error parsing localStorage item "${value}":`, error);
    }
  }

  localStorage<K extends LocalStoreDataKeys>(
    action: 'get',
    key: K,
  ): LocalStoreDataMap[K] | undefined;
  localStorage<K extends LocalStoreDataKeys>(
    action: 'set',
    key: K,
    data: LocalStoreDataMap[K]
  ): undefined;
  localStorage<K extends LocalStoreDataKeys>(
    action: 'remove',
    key: K,
  ): undefined;
  localStorage(
    action: 'set'|'get'|'remove',
    key: LocalStoreDataKeys,
    data?: LocalStoreDataMap[LocalStoreDataKeys]
  ): LocalStoreDataMap[LocalStoreDataKeys] | undefined {
    // console.log('en local storage: action - key - data',action,key,data )
    if (action == 'set') {
      // console.log('en SET')
      if (data == null) {
        return undefined;
      }

      let dataToStore: unknown = data;
      // Convertir Map a Array para que stringify no pierda los datos
      if (key === 'LogicChain') {
        const logicChainData = data as LogicChain;
        if (logicChainData.logicMap instanceof Map) {
          dataToStore = {
            ...logicChainData,
            logicMap: Array.from(logicChainData.logicMap.entries())
          } as unknown;
        }
      }

      localStorage.setItem( key, JSON.stringify(dataToStore) );
      // console.log('data guardada')

    } else if (action == 'get') {
      // console.log('en GET')
      const rawData = localStorage.getItem(key);
      if (!rawData) {
        return undefined;
      }
      const data = this.JSONtryParse<unknown>(rawData);
      if (!data || typeof data !== 'object') {
        return undefined;
      }

      if (key === 'LogicChain') {
        const logicChainData = data as { [k: string]: unknown };
        // Reconstruir el Map desde el Array
        if (logicChainData['logicMap'] && Array.isArray(logicChainData['logicMap'])) {
          logicChainData['logicMap'] = new Map(logicChainData['logicMap'] as [string, unknown][]);
        }
        return logicChainData as unknown as LogicChain;
      } else if (key === 'userLogged') {
        return data as unknown as UserLogged;
      } else if (key === 'proneFirstLoadStorage') {
        return data as unknown as ProneFirstLoad;
      }

    } else if (action == 'remove') {
      // console.log('en REMOVE')
      localStorage.removeItem(key);
    }

    return undefined;
  }
}
